import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CIPXDrawerContentInterface } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-content.interface';
import { DRAWER_DATA, DRAWER_REF, DrawerRef } from '@sbd-doug-frontend/shared/lib/ui/drawer/drawer-manager.service';
import { AccountManagerActivityService } from './account-manager-activity.service';
import { CommonDrawerFooterComponent } from '@sbd-doug-frontend/shared/lib/shared-components/common-drawer-footer/common-drawer-footer.component';
import { AccountManagerActivityCallFormModule } from './account-manager-activity-call-form.module';
import { AccountManagerActivityCallFormComponent } from './account-manager-activity-call-form.component';
import { AccountManagerActivityFormService } from './account-manager-activity-form.service';
import { UserService } from '@sbd-doug-frontend/app/core/user/user.service';
import { TaskCreateFormDraft } from '@sbd-doug-frontend/shared/lib/shared-components/task-viewer/content/right-content/stage-column/forms/task-create-form/task-create-form.component';
import {
  ActivityActor,
  ActivityCallFormDraft,
  ActivityCallType,
  CreateCallActivityEventLogInput,
  QuoteRequestEventLog,
  RelatedQuoteRequestRef,
} from '@sbd-doug-frontend/shared/lib/types/activity-viewer/activity-event-log.types';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs';
import { PersistentDrawerTabsService } from '@sbd-doug-frontend/app/layout/services/persistent-drawer-tabs.service';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { AccountService } from '@sbd-doug-frontend/app/modules/accounts/accounts-manager/accounts.service';

export interface AccountManagerActivityDrawerData {
  tabId?: string;
  instanceId: string;
  scopeKey: string;
  accountId?: number | null;
  accountLabel?: string | null;
  activityId?: string | number | null;
  quoteRequestId?: number | null;
  accountUserId?: number | null;
  callDraft?: ActivityCallFormDraft | null;
  drawerCollapsed?: boolean;
  title?: string;
}

@Component({
  selector: 'sbd-doug-frontend-account-manager-activity-drawer-wrapper',
  standalone: true,
  imports: [CommonModule, RouterModule, CommonDrawerFooterComponent, AccountManagerActivityCallFormModule],
  templateUrl: './account-manager-activity-drawer-wrapper.component.html',
  styleUrl: './account-manager-activity-drawer-wrapper.component.scss',
})
export class AccountManagerActivityDrawerWrapperComponent
  implements CIPXDrawerContentInterface<AccountManagerActivityDrawerData>, OnInit, OnDestroy
{
  @Output() closeRequested = new EventEmitter<void>();
  @Output() drawerClosed = new EventEmitter<void>();
  @Output() drawerCollapsed = new EventEmitter<void>();

  _activityService = inject(AccountManagerActivityService);
  _activityFormService = inject(AccountManagerActivityFormService);
  _persistentDrawerTabsService = inject(PersistentDrawerTabsService);
  _accountService = inject(AccountService);
  _userService = inject(UserService);
  _cathyService = inject(CathyService);
  data = inject(DRAWER_DATA, { optional: true }) as AccountManagerActivityDrawerData | null;
  drawerRef = inject(DRAWER_REF, { optional: true }) as DrawerRef<AccountManagerActivityDrawerWrapperComponent> | null;
  componentSelf = this;
  @ViewChild(AccountManagerActivityCallFormComponent) callFormComponent?: AccountManagerActivityCallFormComponent;
  callFormValue: ActivityCallFormDraft = {
    eventTypeId: ActivityCallType.TYPE_RECEIVEPHONE,
    subject: '',
    notes: '',
    actors: [],
    relatedRequests: [],
    createFollowUpTask: false,
  };
  actorOptions: ActivityActor[] = [];
  relatedRequestOptions: RelatedQuoteRequestRef[] = [];
  latestPayload: QuoteRequestEventLog | null = null;
  drawerFooterButtons: Array<{
    id: string;
    label: string;
    color: '' | 'primary' | 'accent' | 'warn';
    action: string;
    enabled: boolean;
    visible: boolean;
  }> = [
    { id: 'save', label: 'Save', color: 'primary', enabled: true, visible: true, action: 'saveActivity' },
    { id: 'cancel', label: 'Cancel', color: '', enabled: true, visible: true, action: 'closeDrawer' },
  ];
  private _tabs$ = toObservable(this._persistentDrawerTabsService.tabs);
  private _unsubscribeAll = new Subject<void>();
  private _relatedRequestSearch$ = new Subject<string>();
  private _dismissAction: 'close' | 'collapse' = 'close';
  private _isSaving = false;

  ngOnInit(): void {
    this.drawerRef?.setTitle('New Call Activity');
    this.drawerRef?.setShowBackButton(false);
    this.drawerRef?.setShowCollapseButton(true);

    this.callFormValue = this._hydrateCallDraft(this.data?.callDraft ?? null);
    this._attachDraftSyncListener();
    this._attachRelatedRequestSearchListener();
    this._hydrateFormLookups();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  canClose(): boolean {
    return true;
  }

  beforeDrawerClosed(): void {
    if (this._dismissAction === 'collapse') {
      this.drawerCollapsed.emit();
    } else {
      this.drawerClosed.emit();
    }
    this._dismissAction = 'close';
  }

  closeDrawer(): void {
    this.closeRequested.emit();
  }

  onCollapseClick(): void {
    this._dismissAction = 'collapse';
  }

  get accountId(): number | null {
    return this.data?.accountId ?? null;
  }

  get accountLabel(): string {
    return this.data?.accountLabel || (this.accountId ? `Account ${this.accountId}` : 'Account');
  }

  get accountOverviewLink(): string | null {
    return this.accountId ? `/accounts/account-manager/${this.accountId}/overview` : null;
  }

  onCallFormValueChanged(value: ActivityCallFormDraft): void {
    this.callFormValue = this._hydrateCallDraft(value);
    this._persistDraftToTabContext();
  }

  onRelatedRequestSearch(query: string): void {
    this._relatedRequestSearch$.next(query);
  }

  saveActivity(): void {
    if (this._isSaving) {
      return;
    }

    const subject = this.callFormValue.subject.trim();
    if (!this.callFormComponent?.validate() || !subject) {
      this._cathyService.error('Subject is required to save an activity.');
      return;
    }

    const accountId = this.data?.accountId ?? null;
    if (!accountId) {
      this._cathyService.error('Unable to save activity: no account selected.');
      return;
    }

    const payloadInput: CreateCallActivityEventLogInput = {
      quoteRequestId: this.data?.quoteRequestId ?? null,
      accountId,
      accountUserId: this.data?.accountUserId ?? null,
      adminUserId: this._userService.currentAdmin()?.id ? Number(this._userService.currentAdmin()?.id) : null,
      eventTypeId: this.callFormValue.eventTypeId,
      subject,
      notes: this.callFormValue.notes.trim(),
      actors: this.callFormValue.actors,
      relatedRequests: this.callFormValue.relatedRequests,
    };

    const payload = this._activityFormService.buildCreateActivityEventLogPayload(payloadInput);
    this.latestPayload = payload;
    console.info('[Account Activity] create event log payload', payload);

    this._setSaving(true);
    this._activityService
      .createActivity(accountId, payload)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (createdActivity) => {
          if (!createdActivity) {
            this._cathyService.error('Activity could not be saved.');
            return;
          }

          const activityId = createdActivity?.event_log_id ? String(createdActivity.event_log_id) : null;
          if (activityId) {
            this._activityService.setSelectedActivityById(activityId);
          }
          this._activityService.refreshActivities();
          this._cathyService.success('Activity saved successfully.');
          const followUpTaskDraft = this.callFormValue.createFollowUpTask
            ? this._buildFollowUpTaskDraft(createdActivity)
            : null;
          this._clearDraftAndClose();
          if (followUpTaskDraft) {
            setTimeout(() => this._openFollowUpTaskDrawer(createdActivity, followUpTaskDraft));
          }
        },
        error: (error) => {
          console.error('[Account Activity] create event log failed', error);
          this._cathyService.error('Failed to save activity.');
          this._setSaving(false);
        },
        complete: () => {
          this._setSaving(false);
        },
      });
  }

  private _hydrateFormLookups(): void {
    const accountId = this.data?.accountId;
    if (!accountId) {
      return;
    }

    this._activityFormService
      .loadActors(accountId, this._userService.currentAdmin())
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((actors) => {
        this.actorOptions = actors;
        this._prefillCurrentAdminActor();
      });

    this._relatedRequestSearch$.next('');
  }

  private _attachRelatedRequestSearchListener(): void {
    const accountId = this.data?.accountId;
    if (!accountId) {
      return;
    }

    this._relatedRequestSearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query) => this._activityFormService.searchRelatedRequests(query, accountId)),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((relatedRequests) => {
        this.relatedRequestOptions = this._mergeRelatedRequestOptions(
          this.callFormValue.relatedRequests,
          relatedRequests
        );
      });
  }

  private _prefillCurrentAdminActor(): void {
    if (this.callFormValue.actors.length > 0) {
      return;
    }

    const defaultActors = [this._getFirstAccountUserActor(), this._getCurrentAdminActor()].filter(
      (actor): actor is ActivityActor => !!actor
    );

    this.callFormValue = this._hydrateCallDraft({
      ...this.callFormValue,
      actors: defaultActors,
    });
    this._persistDraftToTabContext();
  }

  private _getCurrentAdminActor(): ActivityActor | null {
    const currentAdminId = this._userService.currentAdmin()?.id;
    if (!currentAdminId) {
      return null;
    }

    return (
      this.actorOptions.find(
        (actor) => actor.actor_scope_key === 'xbadmin' && actor.actor_id === Number(currentAdminId)
      ) ?? null
    );
  }

  private _getFirstAccountUserActor(): ActivityActor | null {
    return this.actorOptions.find((actor) => actor.actor_scope_key === 'cipxaccountuser') ?? null;
  }

  private _mergeRelatedRequestOptions(
    selectedRequests: RelatedQuoteRequestRef[],
    searchResults: RelatedQuoteRequestRef[]
  ): RelatedQuoteRequestRef[] {
    const merged = new Map<number, RelatedQuoteRequestRef>();
    [...selectedRequests, ...searchResults].forEach((request) => {
      merged.set(request.quote_request_id, request);
    });
    return Array.from(merged.values());
  }

  private _clearDraftAndClose(): void {
    this._clearDraftFromTabContext();
    this.callFormValue = {
      eventTypeId: ActivityCallType.TYPE_RECEIVEPHONE,
      subject: '',
      notes: '',
      actors: [],
      relatedRequests: [],
      createFollowUpTask: false,
    };
    this.closeDrawer();
  }

  private _setSaving(isSaving: boolean): void {
    this._isSaving = isSaving;
    this.drawerFooterButtons = this.drawerFooterButtons.map((button) =>
      button.id === 'save' ? { ...button, enabled: !isSaving, label: isSaving ? 'Saving...' : 'Save' } : button
    );
  }

  private _persistDraftToTabContext(): void {
    const tabId = this.data?.tabId;
    if (!tabId) {
      return;
    }
    this._persistentDrawerTabsService.updateTabContext(tabId, {
      accountId: this.accountId,
      accountLabel: this.accountLabel,
      callDraft: this._hydrateCallDraft(this.callFormValue),
    });
  }

  private _clearDraftFromTabContext(): void {
    const tabId = this.data?.tabId;
    if (!tabId) {
      return;
    }
    this._persistentDrawerTabsService.updateTabContext(tabId, {
      callDraft: null,
    });
  }

  private _hydrateCallDraft(draft: ActivityCallFormDraft | null | undefined): ActivityCallFormDraft {
    return {
      eventTypeId: draft?.eventTypeId ?? ActivityCallType.TYPE_RECEIVEPHONE,
      subject: draft?.subject ?? '',
      notes: draft?.notes ?? '',
      actors: Array.isArray(draft?.actors) ? [...draft.actors] : [],
      relatedRequests: Array.isArray(draft?.relatedRequests) ? [...draft.relatedRequests] : [],
      createFollowUpTask: !!draft?.createFollowUpTask,
    };
  }

  private _openFollowUpTaskDrawer(createdActivity: QuoteRequestEventLog, taskDraft: TaskCreateFormDraft): void {
    const accountId = this.accountId;
    if (!accountId) {
      return;
    }

    const activityId = createdActivity.event_log_id ?? Date.now();
    this._persistentDrawerTabsService.openOrFocusTab({
      id: `task-follow-up:account:${accountId}:${activityId}`,
      type: 'task-follow-up',
      title: `Follow-up Task - ${this.accountLabel}`,
      icon: 'heroicons_outline:clipboard-list',
      context: {
        accountId,
        accountLabel: this.accountLabel,
        accountUsers: this._accountService.selectedAccount()?.account_users ?? [],
        sourceActivityId: activityId,
        taskDraft,
      },
    });
  }

  private _buildFollowUpTaskDraft(createdActivity: QuoteRequestEventLog): TaskCreateFormDraft {
    const relatedQuoteRequest = this._getActivityRelatedRequests(createdActivity)[0] ?? null;

    return {
      task_label: createdActivity.event_log_comment || this.callFormValue.subject || 'Activity',
      task_related_entity: relatedQuoteRequest
        ? {
            task_related_entity_type: 'cipquotereq',
            task_related_entity_key: relatedQuoteRequest.quote_request_id,
          }
        : null,
      account_user: [],
      qrAssociatedToTask: relatedQuoteRequest ? this._mapQuoteRequestAssociation(relatedQuoteRequest) : null,
    };
  }

  private _getActivityRelatedRequests(createdActivity: QuoteRequestEventLog): RelatedQuoteRequestRef[] {
    const relatedRequests = createdActivity.event_log_info?.activity?.related_quote_requests;
    if (relatedRequests?.length) {
      return relatedRequests;
    }

    return this.callFormValue.relatedRequests;
  }

  private _mapQuoteRequestAssociation(quoteRequest: RelatedQuoteRequestRef): Record<string, unknown> {
    return {
      quote_request_id: quoteRequest.quote_request_id,
      order_number: quoteRequest.order_number,
      principal_name: quoteRequest.company_name,
      client_name: this.accountLabel,
      bond_label: [
        quoteRequest.bond_amount ? `$${quoteRequest.bond_amount}` : null,
        quoteRequest.state_abbreviation,
        quoteRequest.surety_bond_label,
      ]
        .filter(Boolean)
        .join(' '),
    };
  }

  private _attachDraftSyncListener(): void {
    const tabId = this.data?.tabId;
    if (!tabId) {
      return;
    }

    this._tabs$
      .pipe(
        takeUntil(this._unsubscribeAll),
        map((tabs) => {
          const matchingTab = tabs.find((tab) => tab.id === tabId);
          return (matchingTab?.context?.['callDraft'] as ActivityCallFormDraft | null | undefined) ?? null;
        }),
        distinctUntilChanged((previous, current) => this._draftEquals(previous, current))
      )
      .subscribe((draft) => {
        const nextValue = this._hydrateCallDraft(draft);
        if (this._draftEquals(this.callFormValue, nextValue)) {
          return;
        }
        this.callFormValue = nextValue;
      });
  }

  private _draftEquals(
    left: ActivityCallFormDraft | null | undefined,
    right: ActivityCallFormDraft | null | undefined
  ): boolean {
    return JSON.stringify(this._hydrateCallDraft(left)) === JSON.stringify(this._hydrateCallDraft(right));
  }
}
